import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { CUSTOMER_USER_TYPE } from '../../config/userRoles';
import { Heading, H2, NamedLink, ReviewRating, Reviews } from '../../components';

import css from './ListingPage.module.css';

const roundToHalf = value => Math.round(value * 2) / 2;

/**
 * Product reviews, plus a way in for shoppers who want to leave one.
 *
 * Reviews on Sharetribe are always tied to a completed order — that's what
 * keeps them trustworthy, and it's why there is no "write a review" box for
 * anyone who walks in off the street. So the call to action routes a shopper to
 * the right place instead: their orders if they're signed in, or the shopper
 * sign-up if they're not.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.review>} props.reviews
 * @param {propTypes.error} [props.fetchReviewsError]
 * @param {boolean} [props.isAuthenticated]
 * @returns {JSX.Element}
 */
const SectionReviews = props => {
  const { reviews = [], fetchReviewsError, isAuthenticated } = props;

  const ratings = reviews.map(r => r.attributes?.rating).filter(rating => rating > 0);
  const averageRating = ratings.length
    ? roundToHalf(ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length)
    : null;

  return (
    <section className={css.sectionReviews}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.reviewsTitle" values={{ count: reviews.length }} />
      </Heading>

      {averageRating ? (
        <div className={css.reviewsSummary}>
          <ReviewRating
            rating={Math.round(averageRating)}
            className={css.reviewsSummaryStars}
            reviewStarClassName={css.reviewsSummaryStar}
          />
          <span className={css.reviewsSummaryScore}>{averageRating.toFixed(1)}</span>
          <span className={css.reviewsSummaryCount}>
            <FormattedMessage
              id="ListingPage.reviewsSummaryCount"
              values={{ count: reviews.length }}
            />
          </span>
        </div>
      ) : null}

      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : null}

      {reviews.length === 0 && !fetchReviewsError ? (
        <p className={css.reviewsEmpty}>
          <FormattedMessage id="ListingPage.reviewsEmpty" />
        </p>
      ) : (
        <Reviews reviews={reviews} />
      )}

      <div className={css.reviewCta}>
        <p className={css.reviewCtaText}>
          <FormattedMessage id="ListingPage.reviewCtaText" />
        </p>
        {isAuthenticated ? (
          <NamedLink name="InboxPage" params={{ tab: 'orders' }} className={css.reviewCtaButton}>
            <FormattedMessage id="ListingPage.reviewCtaAuthenticated" />
          </NamedLink>
        ) : (
          <NamedLink
            name="SignupForUserTypePage"
            params={{ userType: CUSTOMER_USER_TYPE }}
            className={css.reviewCtaButton}
          >
            <FormattedMessage id="ListingPage.reviewCtaUnauthenticated" />
          </NamedLink>
        )}
      </div>
    </section>
  );
};

export default SectionReviews;
